<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EChart from './EChart.vue'
import { categoryAxis, chartBase, chartTheme, valueAxis } from './chart-theme.js'
import { useAdminTheme } from '../composables/useAdminTheme.js'

const props = defineProps({ dates: { type: Array, required: true }, series: { type: Array, required: true }, percentage: { type: Boolean, default: false } })
const { t, locale } = useI18n()
const { theme } = useAdminTheme()
const option = computed(() => {
  const colors = chartTheme(theme.value)
  return {
    ...chartBase(colors),
    color: [colors.bar, colors.info, colors.success, colors.warning, colors.danger, colors.textMuted],
    legend: { type: 'scroll', top: 0, textStyle: { color: colors.textMuted } },
    grid: { top: 48, left: 16, right: 16, bottom: 48, containLabel: true },
    xAxis: { ...categoryAxis(colors), boundaryGap: false, data: props.dates },
    yAxis: { ...valueAxis(colors), ...(props.percentage ? { axisLabel: { color: colors.textMuted, formatter: '{value}%' } } : { minInterval: 1 }) },
    ...(props.percentage ? { tooltip: { ...chartBase(colors).tooltip, valueFormatter: value => value === null || value === undefined ? '—' : `${new Intl.NumberFormat(locale.value, { maximumFractionDigits: 2 }).format(value)}%` } } : {}),
    dataZoom: [
      { type: 'inside' },
      {
        type: 'slider', height: 16, bottom: 4, borderColor: colors.border,
        backgroundColor: colors.surface, fillerColor: colors.border,
        handleStyle: { color: colors.surface, borderColor: colors.textMuted },
        moveHandleStyle: { color: colors.textMuted },
        dataBackground: { lineStyle: { color: colors.info }, areaStyle: { color: colors.border } },
        selectedDataBackground: { lineStyle: { color: colors.bar }, areaStyle: { color: colors.border } },
        textStyle: { color: colors.textMuted },
      },
    ],
    series: props.series.map(item => ({
      name: item.label || t(`visualGame.dashboard.metrics.${item.key}`),
      type: 'line', symbol: item.symbol || 'circle', showSymbol: item.values.filter(value => value !== null && value !== undefined).length < 15, connectNulls: false,
      data: item.values, lineStyle: { width: 2, type: item.line_style || 'solid', ...(item.tone ? { color: colors[item.tone] } : {}) },
      ...(item.tone ? { itemStyle: { color: colors[item.tone] } } : {}),
      ...(item.area ? { areaStyle: { color: colors[item.tone || 'success'], opacity: 0.12 } } : {}),
    })),
  }
})
</script>
<template><EChart :option="option" height="320px" /></template>
